import type { CrmAttentionItem, CrmThreadLink } from '@/api/crm';

/** Whose lines the dashboard's attention list shows. */
export type CrmAttentionScope = 'mine' | 'team';

const STORAGE_KEY = 'crm.dashboard.attentionScope';

export const readAttentionScope = (): CrmAttentionScope => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'team' ? 'team' : 'mine';
  } catch {
    return 'mine';
  }
};

export const saveAttentionScope = (scope: CrmAttentionScope) => {
  try {
    localStorage.setItem(STORAGE_KEY, scope);
  } catch {
    // Private windows can refuse storage; the toggle still works.
  }
};

/** Mine = the deals I own, plus lines nobody owns (leads to qualify). */
export const inAttentionScope = (
  item: CrmAttentionItem,
  scope: CrmAttentionScope,
  userId: string | undefined
) => scope === 'team' || !item.ownerUserId || item.ownerUserId === userId;

/**
 * The sentence of a line, as a crm.attention.* key and its values; the
 * component translates the stage and formats the date.
 */
export const attentionMessage = (
  item: CrmAttentionItem
): { key: string; params: Record<string, string | number> } => {
  const days = item.days ?? 0;
  const name = item.contactName || item.replyTo || item.subject || '';
  const title = item.subject ?? '';
  switch (item.kind) {
    case 'support_request':
      return item.companyName
        ? {
            key: 'support_request',
            params: { name, company: item.companyName },
          }
        : { key: 'support_request_no_company', params: { name } };
    case 'email_unanswered':
      return {
        key: 'email_unanswered',
        params: { name, count: Math.max(1, days) },
      };
    case 'meeting_followup':
      return days
        ? { key: 'meeting_followup', params: { name, count: days } }
        : { key: 'meeting_followup_today', params: { name } };
    case 'close_passed':
      return { key: 'close_passed', params: { count: days } };
    case 'close_soon':
      return days
        ? { key: 'close_soon', params: { count: days } }
        : { key: 'close_today', params: {} };
    case 'stalled':
      return { key: 'stalled', params: { count: days } };
    case 'no_next_step':
      return { key: 'no_next_step', params: { count: days } };
    case 'task_overdue':
      return days
        ? { key: 'task_overdue', params: { count: days, title } }
        : { key: 'task_overdue_today', params: { title } };
    case 'task_due':
      if (days === 0) return { key: 'task_due_today', params: { title } };
      if (days === 1) return { key: 'task_due_tomorrow', params: { title } };
      return { key: 'task_due', params: { title } };
    case 'leads_waiting':
      return { key: 'leads_waiting', params: { count: item.count ?? 0 } };
  }
};

/** "Deal · Company", once when the deal is named after its company. */
export const attentionContext = (item: CrmAttentionItem) =>
  [item.dealName, item.companyName]
    .filter(
      (name, index, all): name is string =>
        Boolean(name) && all.indexOf(name) === index
    )
    .join(' · ');

/** "3 hours ago", "yesterday": how long a customer has been waiting. */
export const waitedFor = (at: string, language: string, now = new Date()) => {
  const minutes = Math.max(
    0,
    Math.round((now.getTime() - new Date(at).getTime()) / 60000)
  );
  const format = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  if (minutes < 60) return format.format(-minutes, 'minute');
  if (minutes < 24 * 60)
    return format.format(-Math.floor(minutes / 60), 'hour');
  return format.format(-Math.floor(minutes / (24 * 60)), 'day');
};

/** A reply in the salesperson's own mail app, subject kept. */
export const replyLink = (item: CrmAttentionItem) => {
  if (!item.replyTo) return null;
  const subject = item.subject?.trim() ?? '';
  const re = /^re\s*:/i.test(subject) ? subject : `Re: ${subject}`;
  return `mailto:${encodeURIComponent(item.replyTo)}?subject=${encodeURIComponent(re)}`;
};

/** The app a member answers email in, chosen once per device. */
export type CrmMailApp = 'slipstream' | 'superhuman' | 'gmail' | 'outlook';
export const CRM_MAIL_APPS: CrmMailApp[] = [
  'slipstream',
  'superhuman',
  'gmail',
  'outlook',
];
/** Desktop apps opened by their own link scheme, not in a browser tab. */
export const isDesktopMailApp = (app: CrmMailApp) =>
  app === 'slipstream' || app === 'superhuman';
const MAIL_APP_KEY = 'crm.mailApp';

export const readMailApp = (): CrmMailApp | null => {
  try {
    const value = localStorage.getItem(MAIL_APP_KEY);
    return CRM_MAIL_APPS.includes(value as CrmMailApp)
      ? (value as CrmMailApp)
      : null;
  } catch {
    return null;
  }
};

export const saveMailApp = (app: CrmMailApp) => {
  try {
    localStorage.setItem(MAIL_APP_KEY, app);
  } catch {
    // Private windows can refuse storage; Reply asks again next time.
  }
};

/** Which synced email a line's Reply opens. */
export const replySource = (item: CrmAttentionItem) =>
  item.kind === 'support_request'
    ? ('support' as const)
    : item.kind === 'email_unanswered'
      ? ('activity' as const)
      : null;

/**
 * The conversation itself in the member's mail app, so a reply stays in
 * the thread (JP: a new message loses the context). Null when the app
 * cannot open it; Reply then falls back to a new email.
 */
export const mailAppLink = (app: CrmMailApp, link: CrmThreadLink) => {
  if (link.provider === 'microsoft' || app === 'outlook') return link.webLink;
  if (!link.threadId) return null;
  // Slipstream also writes the reply with its AI (JP: "autoreply"); it
  // stays a draft until the member sends it.
  if (app === 'slipstream')
    return `slipstream://${link.mailboxEmail}/thread/${link.threadId}?draft=ai`;
  return app === 'superhuman'
    ? `superhuman://${link.mailboxEmail}/thread/${link.threadId}`
    : `https://mail.google.com/mail/?authuser=${encodeURIComponent(link.mailboxEmail)}#all/${link.threadId}`;
};
